import { NavLink, useNavigate, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "@/orbit/context/AuthContext";
import { LayoutDashboard, Building2, Users, Megaphone, Activity, Handshake, BarChart3, Settings, LogOut, ChevronRight } from "lucide-react";

const nav = [
  { to: "/orbit/app/dashboard", label: "Dashboard", icon: LayoutDashboard, phase: 0 },
  { to: "/orbit/app/accounts", label: "Accounts", icon: Building2, phase: 1, disabled: true },
  { to: "/orbit/app/contacts", label: "Contacts", icon: Users, phase: 1, disabled: true },
  { to: "/orbit/app/campaigns", label: "Campaigns", icon: Megaphone, phase: 2, disabled: true },
  { to: "/orbit/app/activities", label: "Activities", icon: Activity, phase: 2, disabled: true },
  { to: "/orbit/app/deals", label: "Deals", icon: Handshake, phase: 3, disabled: true },
  { to: "/orbit/app/reports", label: "Reports", icon: BarChart3, phase: 5, disabled: true },
];

export default function AppShell() {
  const { user, workspace, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const currentLabel = (() => {
    if (location.pathname.includes("/settings")) return "Settings";
    if (location.pathname.includes("/dashboard")) return "Dashboard";
    return "Orbit";
  })();

  const handleLogout = async () => {
    await logout();
    navigate("/orbit/login", { replace: true });
  };

  const initials = `${user?.first_name?.[0] || "?"}${user?.last_name?.[0] || ""}`.toUpperCase();

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="orbit-app-shell">
      <aside className="w-64 bg-[#0A1F44] text-white flex flex-col" data-testid="orbit-sidebar">
        <div className="px-6 py-6 border-b border-white/10">
          <div className="text-[11px] uppercase tracking-[0.22em] text-blue-300 font-semibold">Orbit CRM</div>
          <div className="mt-1 text-lg font-semibold truncate" data-testid="orbit-workspace-name">{workspace?.name || "Workspace"}</div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {nav.map(({ to, label, icon: Icon, disabled, phase }) => (
            <NavLink
              key={to}
              to={disabled ? "#" : to}
              onClick={disabled ? (e) => e.preventDefault() : undefined}
              data-testid={`orbit-nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2 rounded-md text-sm transition ${
                  disabled ? "text-white/40 cursor-not-allowed" :
                  isActive ? "bg-white/10 text-white" : "text-white/75 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              <span className="flex-1">{label}</span>
              {disabled && <span className="text-[10px] text-white/40">P{phase}</span>}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <NavLink
            to="/orbit/app/settings"
            data-testid="orbit-nav-settings"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition ${
                isActive ? "bg-white/10 text-white" : "text-white/75 hover:bg-white/5 hover:text-white"
              }`
            }
          >
            <Settings className="w-4 h-4" />
            <span>Settings</span>
          </NavLink>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-slate-200 px-8 flex items-center justify-between" data-testid="orbit-topbar">
          <div className="text-sm text-slate-500 flex items-center gap-2">
            <span>{workspace?.name}</span>
            <ChevronRight className="w-3 h-3 text-slate-400" />
            <span className="text-slate-700 font-medium" data-testid="orbit-breadcrumb">{currentLabel}</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-sm font-medium text-slate-900" data-testid="orbit-user-name">{user?.first_name} {user?.last_name}</div>
              <div className="text-xs text-slate-500 capitalize">{user?.role?.replace("_", " ")}</div>
            </div>
            <div className="w-9 h-9 bg-[#0A1F44] text-white rounded-full flex items-center justify-center text-xs font-semibold">{initials}</div>
            <button
              onClick={handleLogout}
              data-testid="orbit-logout-btn"
              className="text-slate-500 hover:text-red-600 p-2 rounded-md hover:bg-red-50 transition"
              title="Log out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </header>
        <main className="flex-1 overflow-auto p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
